import useSearchContext from '@hooks/useSearchContext';
import LayoutEntity from '@models/LayoutEntity';

export const useLayoutEntityName = () => {
  const { lcSearchLanguage } = useSearchContext();

  function getLayoutEntityName<T extends LayoutEntity>(entity: T) {
    return typeof entity.name === 'string' ? entity.name : (entity.name?.[lcSearchLanguage] ?? '');
  }

  return getLayoutEntityName;
};

/**
 * Appends a counter to the new item, and trawls through all existing items to ensure that the number is the next in
 * line, and not taken by any other item, i.e. if the provided slug is `slug`, the new value will be `slug-1`, or
 * `slug-2` if `slug-1` is taken, etc...
 */
export function getItemWithRealSlug<T extends { slug: string }>(item: T, allItems: T[]): T {
  const getSlugBase = (slug: string) => {
    const split = slug.split('-');
    if (split.length === 1) return slug;

    const count = split.at(-1);
    return count === '' || count == null || !/^\d+$/.test(count) || Number(count) >= 1000
      ? slug
      : split.slice(0, split.length - 1).join('-');
  };

  const getSlugCount = (slug: string) => {
    const split = slug.split('-');
    if (split.length === 1) return 0;

    const count = split.at(-1);
    return !count || !/^\d+$/.test(count) || Number(count) >= 1000 ? 0 : Number(count);
  };

  const slugBase = getSlugBase(item.slug);

  const counts: number[] = allItems.reduce(
    (acc: number[], cur) =>
      getSlugBase(cur.slug) === slugBase ? [...acc, getSlugCount(cur.slug)] : acc,
    []
  );

  const slug = counts.length === 0 ? slugBase : `${slugBase}-${Math.max(...counts) + 1}`;

  return { ...item, slug };
}

export function getCopiedLayoutEntity<T extends { slug: string }>(layoutEntity: T): T {
  const split = layoutEntity.slug.split('-');
  const count = split.at(-1);

  return {
    ...layoutEntity,
    slug:
      !count || !/^\d+$/.test(count) || Number(count) >= 1000
        ? `${layoutEntity.slug}-1`
        : [...split.slice(0, split.length - 1), Number(count) + 1].join('-'),
  };
}

/**
 * Removes attributes related to updates (`updatedBy` and `updatedDateTime`) for comparison purposes
 */
export function removeCrudAttrs<T extends LayoutEntity>(layoutEntity: T): Partial<T> {
  const tmp: Partial<T> = { ...layoutEntity };
  delete tmp.updatedBy;
  delete tmp.updatedDateTime;
  return tmp;
}
